import useCharityMeals from '@/helpers/features/charityMeals'
import InfoIcon from '@mui/icons-material/Info'
import { Box, Divider, Skeleton, Stack, Tooltip, Typography } from '@mui/material'

import { debounce } from 'lodash'
import type React from 'react'
import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import BasketItem from './basket-item'

type ItemOption = {
  id: string
  name: string
  priceExTax: string
  qty: number
  type: string
}
export type Item = {
  id: string
  coreItemId?: string
  itemOptions: ItemOption[]
  minimumQuantity: number
  name: string
  note: string
  price: string
  quantity: number
  acknowledgedAt?: string
  updatedAt?: string
  updatedByVendor?: boolean
  canUpdateQty: boolean
  canDelete: boolean
  /** Team-order lines only — the mapper strips these on standard baskets. */
  addedByUserId?: number | null
  addedByName?: string | null
  qtyLockedForCateringTeamOrder?: boolean
  /** Custom items have no itemReference in basket.items, so
      updateItemInBasket can't target it. Notes are read-only for them. */
  isCustomItem?: boolean
}

type Items = Array<Item>

export type SummaryData = {
  itemCount: number
  charityContributionHeadcount: string
  friendlyId: string
  subtotalExTax: string
  totalTaxValue?: string
  totalExTax?: string
  totalIncTax?: string
  grandTotal: string
}

type Fee = {
  code: string
  description: string
  feeTotalExTax: string
}

type Fees = Array<Fee>

type Tax = {
  rate: string
  totalExTax: string
}

type TaxRates = Array<Tax>

export type ConsolidatedBasketProps = {
  id: string
  fees: Fees
  taxRates: TaxRates
  headerSection: React.ReactElement
  footerSection: React.ReactElement
  errorSection: React.ReactElement
  items: Items
  summary: SummaryData
  taxAcronym: string
  dataIsUpdating: boolean
  loadingNewData?: boolean
  maxHeight: string
  onDeleteClick: (itemId: string) => void
  onQuantityChange: (itemId: string, qty: number) => void
  onNumberedOptionQuantityChange: (itemId: string) => void
  onNoteChange: (itemId: string, note: string) => void
  showItemNotes: boolean
  inputBarSection?: React.ReactNode
  unavailableItemIds?: string[]
}

const ConsolidatedBasket: React.FC<ConsolidatedBasketProps> = ({
  fees,
  taxRates,
  headerSection,
  footerSection,
  errorSection,
  items,
  summary,
  taxAcronym,
  dataIsUpdating,
  loadingNewData = false,
  maxHeight,
  onDeleteClick,
  onQuantityChange,
  onNumberedOptionQuantityChange,
  onNoteChange,
  showItemNotes,
  inputBarSection,
  unavailableItemIds = [],
}) => {
  const isEmptyBasket = items.length < 1
  const charityMealsEnabled = useCharityMeals()
  const { t } = useTranslation()

  const debouncedOnQuantityChange = useCallback(
    debounce((itemId: string, qty: number) => {
      onQuantityChange(itemId, qty)
    }, 100),
    [onQuantityChange],
  )

  useEffect(() => {
    return () => {
      debouncedOnQuantityChange.cancel()
    }
  }, [debouncedOnQuantityChange])

  return (
    <Stack
      sx={{ maxHeight, display: 'flex', flexDirection: 'column' }}
      bgcolor={'primary.main'}
      borderRadius={4}>
      {headerSection}
      <Stack
        gap={2}
        overflow={'hidden'}
        sx={{
          padding: 2,
          paddingTop: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
        {inputBarSection}
        <Box
          sx={{
            overflow: 'auto',
            flexGrow: 1,
            bgcolor: 'background.paper',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
          }}>
          {loadingNewData ? (
            <Box
              sx={{ paddingX: 1, paddingBottom: 1, height: '150px' }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              gap={1}>
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
            </Box>
          ) : null}

          {isEmptyBasket && !loadingNewData ? (
            <Box
              sx={{ paddingX: 1, paddingBottom: 1, height: '150px' }}
              display="flex"
              justifyContent="center"
              alignItems="center">
              <Typography variant="body2" align="center">
                {t('marketplace:yourBasketIsEmpty')}
              </Typography>
            </Box>
          ) : null}

          {!isEmptyBasket && !loadingNewData ? (
            <>
              {/* Scrollable content area */}
              <Box
                sx={{
                  paddingX: 1,
                  paddingTop: 1,
                  flexGrow: 1,
                  overflow: 'auto',
                  scrollbarWidth: 'thin',
                }}>
                {items.map((item: Item) => {
                  const isUnavailable = unavailableItemIds.includes(item.id)
                  return (
                    <BasketItem
                      key={item.id}
                      onDeleteClick={onDeleteClick}
                      onQuantityChange={(_itemReference, newQuantity) => {
                        const hasNumberOption = item.itemOptions.some((option) => {
                          return option.type === 'numbers'
                        })

                        if (hasNumberOption && item.quantity < newQuantity) {
                          if (!item.coreItemId) return

                          onNumberedOptionQuantityChange(item.coreItemId)
                        } else {
                          debouncedOnQuantityChange(item.id, newQuantity)
                        }
                      }}
                      onNoteChange={onNoteChange}
                      showItemNotes={showItemNotes}
                      {...item}
                      canDelete={item.canDelete}
                      canUpdateQty={item.canUpdateQty}
                      isUnavailable={isUnavailable}
                    />
                  )
                })}

                {/* Breakdown section - scrollable */}
                <Stack spacing={1} paddingX={0} paddingY={1}>
                  <Stack direction="column" justifyContent="space-between">
                    {taxRates?.map((tax) => {
                      return (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          key={tax.rate}
                          paddingY={0.5}>
                          <Typography variant="caption">
                            {t('marketplace:itemSubtotal')} ({taxAcronym} @ {tax.rate}%)
                          </Typography>

                          <Typography variant="body3">
                            {dataIsUpdating ? (
                              <Skeleton variant="text" width={60} />
                            ) : (
                              `${tax.totalExTax}`
                            )}
                          </Typography>
                        </Stack>
                      )
                    })}

                    {fees?.map((fee) => {
                      const convertedTotal = fee.feeTotalExTax
                      return (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          key={fee.code}
                          paddingY={0.5}>
                          <Typography variant="caption">{fee.description}</Typography>
                          <Typography variant="body3">
                            {dataIsUpdating ? (
                              <Skeleton variant="text" width={60} />
                            ) : (
                              `${convertedTotal}`
                            )}
                          </Typography>
                        </Stack>
                      )
                    })}

                    <Stack direction="row" justifyContent="space-between" paddingY={0.5}>
                      <Typography variant="caption">
                        {t('marketplace:total')} {taxAcronym}
                      </Typography>

                      <Typography variant="body3">
                        {dataIsUpdating ? (
                          <Skeleton variant="text" width={60} />
                        ) : (
                          `${summary?.totalTaxValue}`
                        )}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider />

                  {charityMealsEnabled.enabled && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems={'center'}
                      paddingY={0.5}>
                      <Typography variant="caption">
                        {t('marketplace:sinceYoureFeedingWell', {
                          charityContributionHeadcount: summary?.charityContributionHeadcount,
                        })}
                      </Typography>

                      <Tooltip title={t('marketplace:forEveryMealYou')} placement="left">
                        <InfoIcon color="info" sx={{ transform: 'scale(0.75)' }} />
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              </Box>

              {/* Fixed footer section */}
              <Box sx={{ flexShrink: 0, bgcolor: 'background.paper', borderRadius: '0 0 8px 8px' }}>
                <Box paddingX={1}>{errorSection}</Box>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                  spacing={2}
                  padding={1}>
                  {footerSection}
                  <Stack direction={'column'} flexGrow={1} alignItems={'flex-end'}>
                    <Stack direction={'row'} spacing={1} alignItems={'baseline'}>
                      <Typography variant="h5" sx={{ fontSize: 12 }}>
                        {t('marketplace:total')}
                      </Typography>

                      <Typography variant="caption" sx={{ fontSize: 10 }}>
                        ({t('marketplace:inc', { taxAcronym })})
                      </Typography>
                    </Stack>

                    <Typography variant="h5" sx={{ fontSize: 12 }}>
                      {dataIsUpdating ? (
                        <Skeleton variant="text" width={80} />
                      ) : (
                        `${summary.grandTotal}`
                      )}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </>
          ) : null}
        </Box>
      </Stack>
    </Stack>
  )
}

export default ConsolidatedBasket
